import { CATALOGUE_SLOT_OF, COMBAT_SETS, isBarePiece, type CombatSet, type LookPiece } from "../rules/look-records.ts";
import { catalogueItem, type LookCatalogue } from "./look-catalogue.ts";

/**
 * Which combat sets a piece of the looks table covers, read from the character sprite catalogue (ADR-094 decisions 18
 * and 19). The table states each piece's `sets` as text; the catalogue, written by the converter from the packs'
 * pixels, says which animations each item has frames for. `coverageProblems` compares the two, so a piece can neither
 * claim a set it cannot play nor leave out one it can.
 *
 * What a set plays: the movement animations the game cuts a strip from (stand, walk, run), and the set's own
 * animations that it cuts a strip from. A set the game cuts nothing from yet (bow and spear, until their weapons ship)
 * asks for every animation the catalogue lists under it, so its claim is the strictest one until a row names its
 * strips. A body layer covers a set when it has frames for all of them; a hand item covers the set it belongs to.
 */

/**
 * The animations a look must have frames for to play a combat set.
 *
 * @param catalogue The catalogue.
 * @param set The combat set.
 * @returns The animations' ids, in the catalogue's order: the movement animations with a game strip, then the set's
 *   animations with a game strip, or every animation of the set when none has one.
 * @example
 * setAnimations(catalogue, "one-hand");
 * // ["stand", "walk", "run", "one-hand-hit", "one-hand-knockdown", "one-hand-idle", ...]
 */
export function setAnimations(catalogue: LookCatalogue, set: CombatSet): string[] {
  const movement = catalogue.animations.filter((animation) => animation.set === null && animation.gameStrip !== null);
  const own = catalogue.animations.filter((animation) => animation.set === set);
  const played = own.filter((animation) => animation.gameStrip !== null);
  return [...movement, ...(played.length > 0 ? played : own)].map((animation) => animation.id);
}

/**
 * The combat sets the catalogue shows an item to cover.
 *
 * @param catalogue The catalogue.
 * @param slot The catalogue's slot id (`CATALOGUE_SLOT_OF` names it for a look slot).
 * @param code The item's code, a piece's `style`.
 * @returns The sets, in `COMBAT_SETS`' order, or null when the catalogue has no such slot or item.
 */
export function provedSets(catalogue: LookCatalogue, slot: string, code: string): CombatSet[] | null {
  const item = catalogueItem(catalogue, slot, code);
  if (item === null) {
    return null;
  }
  if (catalogue.slots.find((candidate) => candidate.id === slot)?.tool === true) {
    return COMBAT_SETS.filter((set) => set === item.set);
  }
  return COMBAT_SETS.filter((set) => setAnimations(catalogue, set).every((animation) => item.animations.includes(animation)));
}

/**
 * Why the catalogue does not show a piece to cover a set it claims.
 *
 * @param catalogue The catalogue.
 * @param piece The piece.
 * @param set The set claimed.
 * @returns The reason, for the end of a problem's line.
 */
function shortfall(catalogue: LookCatalogue, piece: LookPiece, set: CombatSet): string {
  const slot = CATALOGUE_SLOT_OF[piece.slot];
  const item = catalogueItem(catalogue, slot, piece.style);
  if (catalogue.slots.find((candidate) => candidate.id === slot)?.tool === true) {
    return `its set is ${item?.set ?? "none"}`;
  }
  return `no frames for ${setAnimations(catalogue, set)
    .filter((animation) => item?.animations.includes(animation) !== true)
    .join(", ")}`;
}

/**
 * Compares the looks table with the catalogue: every piece must be an item and a colour the catalogue lists, and its
 * `sets` must be exactly the sets the catalogue shows that item to cover. A bare piece is no item of any pack and is
 * passed over: the table's own check holds it to every set.
 *
 * @param pieces The table's pieces.
 * @param catalogue The catalogue the converter wrote.
 * @returns One line per problem, in the table's order and, within a piece, claims before omissions in
 *   `COMBAT_SETS`' order; empty when the table states what the catalogue shows.
 * @example
 * coverageProblems([{ ...alchemistCoat, sets: ["bow"] }], catalogue);
 * // ["alch-1 claims bow, which the catalogue does not show: no frames for bow-draw, bow-sheathe, ..."]
 */
export function coverageProblems(pieces: readonly LookPiece[], catalogue: LookCatalogue): string[] {
  const problems: string[] = [];
  for (const piece of pieces) {
    if (isBarePiece(piece)) {
      continue;
    }
    const slot = CATALOGUE_SLOT_OF[piece.slot];
    const item = catalogueItem(catalogue, slot, piece.style);
    const proved = provedSets(catalogue, slot, piece.style);
    if (item === null || proved === null) {
      problems.push(`${piece.key} is not in the catalogue: ${slot} has no item ${piece.style}`);
      continue;
    }
    if (!item.variants.includes(piece.colour)) {
      problems.push(`${piece.key} is not in the catalogue: ${slot} ${piece.style} has no colour ${piece.colour}`);
      continue;
    }
    for (const set of COMBAT_SETS) {
      if (piece.sets.includes(set) && !proved.includes(set)) {
        problems.push(`${piece.key} claims ${set}, which the catalogue does not show: ${shortfall(catalogue, piece, set)}`);
      }
    }
    for (const set of COMBAT_SETS) {
      if (!piece.sets.includes(set) && proved.includes(set)) {
        problems.push(`${piece.key} omits ${set}, which the catalogue shows`);
      }
    }
  }
  return problems;
}
